import { noop } from 'lodash';
import { memo, useCallback } from 'react';

import { type ActivatedAccount } from '@air/analytics';
import { type Account } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { useToasts } from '@air/provider-toast';
import { getIsSAMLEnforced } from '@air/utils-auth';
import { reportErrorToBugsnag } from '@air/utils-error';

import { EmailSignUpForm, type EmailSignUpSubmitOptions, type SignUpUserType } from '~/components/Auth/EmailSignUpForm';
import { useSignUpWithEmail } from '~/swr-hooks/auth/useSignUpWithEmail';

type SignUpModalEmailFormProps = {
  email: string;
  onSuccess: (account: Account) => void;
  accountCreationSource: ActivatedAccount['creationSource'];
  onBackClick: () => void;
};

const formID = 'sign-up-with-email-modal-form';

export const SignUpModalUserDetailsForm = memo(
  ({ onSuccess, accountCreationSource, email, onBackClick }: SignUpModalEmailFormProps) => {
    const { showToast } = useToasts();
    const { signUpWithEmail } = useSignUpWithEmail();
    const handleSubmit = useCallback(
      async (user: SignUpUserType, { autoVerifyEmail }: EmailSignUpSubmitOptions) => {
        const isSamlEnforced = await getIsSAMLEnforced(user.email);

        if (isSamlEnforced) {
          showToast('You must sign in with SAML SSO for this account', {
            timeBeforeAutoDismiss: undefined,
            type: 'assertive',
            withCloseButton: true,
          });
        } else {
          await signUpWithEmail({
            autoVerifyEmail,
            user,
            setFormError: (error) =>
              showToast(error, { timeBeforeAutoDismiss: null, type: 'assertive', withCloseButton: true }),
            onRegistrationFailure: noop,
            onRegistrationSuccess: (account) => {
              onSuccess(account);
            },
            onUnhandledException: (error) => {
              reportErrorToBugsnag({
                error,
                context: 'Error during signup',
                metadata: { Data: { email: user.email } },
              });
            },
            creationSource: accountCreationSource,
          });
        }
      },
      [accountCreationSource, onSuccess, showToast, signUpWithEmail],
    );

    return (
      <EmailSignUpForm
        initialValues={{ terms: true }}
        defaultEmail={email}
        onSubmit={handleSubmit}
        termsCheckbox={null}
        formID={formID}
      >
        {({ isSubmitting, isValid }) => (
          <div className="mt-12 flex flex-col gap-2">
            <Button
              data-testid="SIGN_UP_WITH_EMAIL_BUTTON"
              type="submit"
              isLoading={isSubmitting}
              size="large"
              color="blue"
              disabled={!isValid || isSubmitting}
            >
              Sign up with email
            </Button>
            <Button
              type="button"
              size="large"
              appearance="ghost"
              color="gray"
              disabled={isSubmitting}
              onClick={onBackClick}
            >
              Back
            </Button>
          </div>
        )}
      </EmailSignUpForm>
    );
  },
);

SignUpModalUserDetailsForm.displayName = 'SignUpModalEmailForm';
